import React from 'react';
import { useTranslation } from 'react-i18next';
import './lynk-logo.css';

/**
 * Lynk — Academic marks.
 *
 * From handoff-academic/logo/LynkLogo.jsx. The marks are TYPE, not images, so
 * in-product we render these components and never an `<img>`: they stay crisp
 * at any size, recolour from one variable, and the tagline stays translatable.
 * The SVG/PNG sets in /public exist only for email, favicons and print handed
 * to a third party.
 *
 * Three marks, and the handoff is specific about which goes where:
 *
 *   <LynkWordmark size={20} />                  dark app nav / top chrome
 *   <LynkSeal size={28} variant="gilt" />       collapsed rail, avatars, icons
 *   <LynkLockup size={96} />                    login / splash
 *   <LynkLockup size={120} />                   marketing hero, CFP landing
 *
 * Two rules that look like bugs and are not:
 *
 *   1. The wordmark is weight 400; the seal is weight 700. 400 at +0.035em is
 *      what makes it read engraved rather than advertising. The seal needs 700
 *      because a single letter at 16px has no other way to hold weight.
 *   2. Minimums: wordmark 96px wide, full lockup 176px wide, seal 16px. Under
 *      176px the tagline rule collapses — drop the tagline, never shrink it.
 *      `LynkLockup` does that for you rather than rendering a broken rule.
 *
 * Colour: `ink` defaults to `currentColor`, so a mark takes the colour of the
 * surface it sits on — chrome-foreground creme in the navbar, primary grenat on
 * a card — with no hex anywhere in this file. Gilt is dark-ground only; it is
 * 2.1:1 on parchment and fails, so never pass it on a light surface.
 */

/**
 * Handoff minimums, in rendered CSS pixels of width.
 *
 * These are checked by MEASURING the mounted element, not by multiplying the
 * font-size by an assumed width-per-em. "Lynk" in Old Standard TT at +0.035em
 * is about 1.9em wide, but that depends on the font actually having loaded —
 * under a Georgia fallback it is different, which is exactly the case where a
 * warning is worth having.
 */
const MIN_WIDTH = { lockup: 176, wordmark: 96 };

/** Dev-only: measure the mark once mounted and complain if it is undersized. */
function useMinWidthWarning(ref, mark, active = true) {
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !active) return;
    const el = ref.current;
    if (!el) return;
    const width = Math.round(el.getBoundingClientRect().width);
    if (width && width < MIN_WIDTH[mark]) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Lynk] ${mark} rendered ${width}px wide; the handoff minimum is ${MIN_WIDTH[mark]}px. ` +
          (mark === 'lockup'
            ? 'Pass tagline={false} or use <LynkWordmark>/<LynkSeal> — never shrink the lockup.'
            : 'Use <LynkSeal> below this size.')
      );
    }
  }, [ref, mark, active]);
}

/**
 * Full lockup: wordmark, then the tagline in EB Garamond italic between two
 * hairlines. Login, splash, marketing hero, programme covers.
 */
export function LynkLockup({ size = 120, ink, tagline = true, className = '', style, ...rest }) {
  const { t } = useTranslation('common');
  const ref = React.useRef(null);
  // Only the tagline variant has a hard width floor — that's the width at which
  // its hairline rule collapses. The wordmark-only lockup has its own floor.
  useMinWidthWarning(ref, tagline ? 'lockup' : 'wordmark');

  const showTagline = tagline;

  return (
    <span
      {...rest}
      ref={ref}
      className={`lynk-lockup ${className}`}
      style={{
        '--lynk-mark-size': `${size}px`,
        ...(ink ? { '--lynk-mark-ink': ink } : { '--lynk-mark-ink': 'currentColor' }),
        ...style,
      }}
      aria-label={showTagline ? `Lynk — ${t('brand.tagline', 'Connecting people and ideas')}` : 'Lynk'}
      role="img"
    >
      <span className="lynk-lockup__word" aria-hidden="true">
        Lynk
      </span>
      {showTagline && (
        <span className="lynk-lockup__rule" aria-hidden="true">
          <span className="lynk-lockup__tagline">
            {t('brand.tagline', 'Connecting people and ideas')}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * Wordmark alone. All app chrome, and anything under ~40px tall where the
 * lockup would be illegible.
 *
 * Note on the 96px floor: the handoff states a 96px-wide wordmark minimum, but
 * its own decision table prescribes `size={20}` for the app nav bar, which
 * renders about 38px wide. Both cannot hold. Read here as: 96px is the brand and
 * print floor, and app chrome is the documented exception — so this component
 * does not warn, because a warning on every navbar render is noise. Raised with
 * the user rather than silently picking one.
 */
export function LynkWordmark({ size = 20, ink, className = '', style, ...rest }) {
  return (
    <span
      {...rest}
      className={`lynk-wordmark ${className}`}
      style={{
        fontSize: `${size}px`,
        ...(ink ? { '--lynk-mark-ink': ink } : null),
        ...style,
      }}
      aria-label="Lynk"
      role="img"
    >
      Lynk
    </span>
  );
}

const SEAL_VARIANTS = ['grenat', 'gilt', 'night', 'outline'];

/**
 * The initial on a square plate. The only mark that survives to 16px, so it
 * carries favicons, avatar fallbacks, notification icons and the collapsed rail.
 *
 * `variant="gilt"` and `"night"` are dark-ground only.
 */
export function LynkSeal({ size = 96, variant = 'grenat', className = '', style, ...rest }) {
  const v = SEAL_VARIANTS.includes(variant) ? variant : 'grenat';
  return (
    <span
      {...rest}
      className={`lynk-seal ${v === 'grenat' ? '' : `lynk-seal--${v}`} ${className}`}
      style={{ '--lynk-seal-size': `${size}px`, ...style }}
      aria-label="Lynk"
      role="img"
    >
      <span aria-hidden="true">L</span>
    </span>
  );
}

export default LynkLockup;
